import { HttpResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { AddOnDetail } from 'src/app/classes/add-on-detail';
import { AssignTask } from 'src/app/classes/assign-task';
import { CreateAddOnPayload } from 'src/app/classes/create-addon-payload';
import { Dispatch } from 'src/app/classes/dispatch';
import { DispatchDriverSummary } from 'src/app/classes/dispatch-driver-summary';
import { DropOffTask } from 'src/app/classes/dropoff-task';
import { Passenger } from 'src/app/classes/passenger';
import { PickUpTask } from 'src/app/classes/pickup-task';
import { Profile } from 'src/app/classes/profile';
import { RemoveAddOnPayload } from 'src/app/classes/remove-addon-payload';
import { SearchSummary } from 'src/app/classes/search-summary';
import { DispatchService } from 'src/app/services/dispatch.service';
import { EventsDeltaService } from 'src/app/services/events-delta.service';
import { EventsService } from 'src/app/services/events.service';
import { LoggerService } from 'src/app/services/logger.service';
import { MyTasksService } from 'src/app/services/my-tasks.service';
import { ProfileService } from 'src/app/services/profile.service';
import { SearchService } from 'src/app/services/search.service';
import { ToastService } from 'src/app/services/toast.service';
import { UnassignedTasksService } from 'src/app/services/unassigned-tasks.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { ConfirmPassengerNotCheckedInDialogComponent } from '../confirm-passenger-not-checked-in-dialog/confirm-passenger-not-checked-in-dialog.component';
import { MyTaskConfirmationDialogComponent } from '../my-tasks/my-task-confirmation-dialog/my-task-confirmation-dialog.component';

const enum TaskActionType {
  PICKUP = 'pickup',
  DROPOFF = 'dropoff'
}
export enum PickUpDropOffType {
  PICKUP_ALL = 'pickupAll',
  PICKUP_SELECTED = 'pickupSelected',
  DROPOFF_ALL = 'dropoffAll',
  DROPOFF_SELECTED = 'dropoffSelected'
}

@Component({
  standalone: false,
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit {
  PickUpDropOffType = PickUpDropOffType;

  warningIcon = faExclamationTriangle as IconProp;
  public taskGroupSummaryList: Array<SearchSummary>;
  driverTasksForm: FormGroup;
  searchForm = new FormGroup({
    searchCategory: new FormControl('Flight', Validators.nullValidator),
    airlinePrefix: new FormControl('UPS', Validators.required),
    searchTerm: new FormControl('',
    Validators.compose([
      Validators.required,
      Validators.pattern("^[0-9]*$"),
      Validators.minLength(1),
      Validators.maxLength(4)
    ])),
  });

  public validationErrMsg:string;

  public dispatchData: Dispatch;
  // public preferredDriversList: Array<string>;
  unsubscribe$: Subject<boolean> = new Subject();
  showAdd: boolean;
  addItemTypes: any[];
  profile: Profile;
  flightCustoms: Array<string>;
  expandedPanel = '-1';
  currentTaskToProcess = '';
  loading = false;
  pickUpDropOffAll = false;
  partialPassengerDropoff = false;
  partialAddOnDropoff = false;
  driverSummaryList: Array<DispatchDriverSummary>;

  constructor(private searchService: SearchService,
    private dispatchService: DispatchService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private eventsService: EventsService,
    public profileService: ProfileService,
    private eventsDeltaService: EventsDeltaService,
    public dialog: MatDialog,
    private logger: LoggerService,
    private toastService: ToastService,
    private myTasksService: MyTasksService,
    private unassignedTasksService: UnassignedTasksService,

    private utilityService: UtilitiesService) { }

  ngAfterContentChecked(): void {
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.subscribeToDispatchData();
    this.subscribeSearchFormChange();
    this.buildForm();
    this.subscribeToEvents();
    this.subscribeToEventDeltas();
    this.profileService.getProfileObs().subscribe(profile=>{
      this.profile = profile;
    });
    this.addItemTypes = [
      {name: "Passenger", value: "Passenger"},
      {name: "Paperwork", value: "Paperwork"},
      {name: "Catering", value: "Catering"},
      {name: "Other", value: "Other"},
    ]
  }

  subscribeToEventDeltas(): void {
    this.eventsDeltaService.getEventData().pipe(takeUntil(this.unsubscribe$)).subscribe((events) => {
      this.currentTaskToProcess = '';
      this.pickUpDropOffAll = false;
      this.loading = false;
    });
  }

  buildForm(): void {
    this.driverTasksForm = this.fb.group({
      cordNotes: this.fb.control(''),
      driverNotes: this.fb.control(''),
      taskDetails: this.fb.group({}),
      passengers: this.fb.group({})
    });
  }

  toggleAddItem(status:boolean):void{
    this.showAdd =status;
  }

  get taskDetails(): FormGroup {
    return this.driverTasksForm.get('taskDetails') as FormGroup;
  }

  newValueTaskDetails(type: string, value: string): FormGroup {
    return this.fb.group({
      type: type,
      value: value,
      isActive: false
    });
  }

  populateAndAddTaskDetails(taskName: string, type: string, value: string, e: any): void {
    if(type === undefined){
      this.toastService.show(`Form Error: Please select the type of add-on`, { classname: 'bg-danger text-light', delay: 4000 });
      return;
    }

   const detailsId: string = this.utilityService.guid();
   const payload: CreateAddOnPayload = {
     type: 'createAddOn',
     updatedBy: this.profileService.profileObs$.getValue().adId,
     newAddOn: {
       id: detailsId,
       type: type,
       name: value
     }
   }
    this.myTasksService.createAddOn(payload, taskName)
    .pipe(tap(() => this.eventsDeltaService.getDeltas()))
    .subscribe((resp:HttpResponse<any>) => {
      this.logger.debug(`PUT createAddOn response:`, resp, 'http');
      console.log(`PUT createAddOn response:`, resp);
       if(resp.status === 204){
        // add the new slider control to the form
        this.taskDetails.addControl(
          this.getDriverPassengerFormKey(taskName, detailsId),
          this.newValueTaskDetails(type, value)
        );
        this.taskGroupSummaryList.forEach((driverTask: SearchSummary) => {
          if (driverTask.taskName === taskName) {
            driverTask.addOnDetails = driverTask.addOnDetails || [];
            driverTask.addOnDetails.push({ id: detailsId, type, name: value });
          }
        });
        this.showAdd = false;
        e.stopImmediatePropagation();
        this.toastService.show(`Success: Task Updated`, { classname: 'bg-success text-light', delay: 4000 });
      }else{
        this.toastService.show(`Error: ${resp.status} occurred, task was not updated!`, { classname: 'bg-danger text-light', delay: 4000 });
      }
    });
  }

  onRmoveTaskDetails(taskName: string, detailId: number | string): void {
    this.dialog.open(MyTaskConfirmationDialogComponent,{
      width: '550px',
      data: {
        message: 'Are you sure you want to delete this item?'
      }
    }).afterClosed().subscribe(response => {
      if (response) {
        this.removeTaskDetails(taskName, detailId);
      }
    });
  }

  removeTaskDetails(taskName: string, detailId: number | string): void {
    const payload: RemoveAddOnPayload = {
      type: 'deleteAddOn',
      deleteAddOnId: detailId,
      deleteBy: this.profileService.profileObs$.getValue().adId
    }
    this.myTasksService.removeAddOn(payload, taskName)
    .pipe(tap(() => this.eventsDeltaService.getDeltas()))
    .subscribe((resp:HttpResponse<any>) => {
      this.logger.debug(`PUT removeAddOn response:`, resp, 'http');
       if(resp.status === 204){
        // remove the control to the form
        this.taskGroupSummaryList.forEach((driverTask: SearchSummary) => {
          if (driverTask.taskName === taskName) {
            driverTask.addOnDetails = driverTask.addOnDetails.filter((details: AddOnDetail) => details.id !== detailId);
          }
        });
        this.taskDetails.removeControl(this.getDriverPassengerFormKey(taskName, detailId));
        this.toastService.show(`Success: Task Updated`, { classname: 'bg-success text-light', delay: 4000 });
      }else{
        this.toastService.show(`Error: ${resp.status} occurred, task was not updated!`, { classname: 'bg-danger text-light', delay: 4000 });
      }
    });
  }

  private subscribeToEvents(): void {
    this.eventsService.getEventData().pipe(takeUntil(this.unsubscribe$))
    .subscribe((events) => {
        if (events) {
          this.flightCustoms = events.uiParameters.FLIGHT_CUSTOMS;
        }
      });
  }

  private subscribeToDispatchData(){
    this.dispatchService.getDispatchData()
    .pipe(
      takeUntil(this.unsubscribe$))
        .subscribe(dispatchData => {
          this.dispatchData = dispatchData;
          this.driverSummaryList = this.dispatchData['driverSummaryList'];
          if(this.taskGroupSummaryList?.length>50){
            this.taskGroupSummaryList = this.taskGroupSummaryList.slice(0,50);
          }
        }
    );
  }

  get f(){
    return this.searchForm.controls;
  }

  driverSummaryChange(event: MatSelectChange):void{
    alert('Selction changed')
    // this.dispatchForm.get('assignedDriver').setValue(event.value);
    //make call
    // this.dispatchForm.get('driverSummary').setValue('');
  }

  getStatusColor(taskGroupStatus): string{
    return this.utilityService.getStatusColor(taskGroupStatus);
  }


  getdueTime(date: string): any {
    return this.utilityService.timeMath(date);
  }

  updateDriverTaskForm(driverTasks: SearchSummary[]): void {
    // Updates the passengers form group by adding/removing controls as the list of passengers are updated

    // Constructs a list of form control keys that are in the format `${driverTask.taskName}:${passenger.id}`
    const passengerKeys: string[] = [];
    driverTasks.forEach((driverTask: SearchSummary) => {
      if (driverTask.passengers) {
        driverTask.passengers.forEach((passenger: Passenger) =>
          passengerKeys.push(this.getDriverPassengerFormKey(driverTask.taskName, passenger.id))
        );
      }
    });

    // Adds new form controls for passengers that didn't exist before
    passengerKeys.forEach((passengerKey: string) => {
      if (!(this.driverTasksForm.controls['passengers'] as FormGroup).contains(passengerKey)) {
        (this.driverTasksForm.controls['passengers'] as FormGroup).addControl(passengerKey, this.fb.control(false));
      }
    });

    // Removes any form controls for passengers that are no longer in the driverTasks
    Object.keys(this.driverTasksForm.controls['passengers']).forEach((formKey: string) => {
      if (!passengerKeys.includes(formKey)) {
        (this.driverTasksForm.controls['passengers'] as FormGroup).removeControl(formKey);
      }
    });

    // this.driverTasksForm.get('cordNotes').setValue(driverTasks[0].specialInstructions);
    // this.driverTasksForm.get('driverNotes').setValue(driverTasks[0].driverNotes);
  }

  updateDriverTaskFormAddOns(driverTasks: SearchSummary[]): void {
    const addOnKeys: string[] = [];
    driverTasks.forEach((driverTask: SearchSummary) => {
      if (driverTask.addOnDetails) {
        driverTask.addOnDetails.forEach((addOnDetails: AddOnDetail) =>
          addOnKeys.push(this.getDriverPassengerFormKey(driverTask.taskName, addOnDetails.id))
        );

        driverTask.addOnDetails.forEach((details: AddOnDetail) => {
          const addOnKey = this.getDriverPassengerFormKey(driverTask.taskName, details.id);

          if (!(this.driverTasksForm.controls['taskDetails'] as FormGroup).contains(addOnKey)) {
            const fg: FormGroup = this.newValueTaskDetails(details.type, details.name);

            (this.driverTasksForm.controls['taskDetails'] as FormGroup).addControl(addOnKey, fg);
          }
        });

        Object.keys(this.driverTasksForm.controls['taskDetails']).forEach((formKey: string) => {
          if (!addOnKeys.includes(formKey)) {
            (this.driverTasksForm.controls['taskDetails'] as FormGroup).removeControl(formKey);
          }
        });
      }
    });
  }


  getDriverPassengerFormKey(driverTaskName: string, passengerId: number | string): string {
    return `${driverTaskName}:${passengerId}`;
  }

  getPassengerFormControl(driverTaskName: string, passengerId: number | string, status: string): FormControl {
    return (this.driverTasksForm.controls['passengers'] as FormGroup).controls[
      this.getDriverPassengerFormKey(driverTaskName, passengerId)
    ] as FormControl;
  }

  getDetailsFormControl(driverTaskName: string, status:string, detailsId: number | string): FormControl {
    return (this.driverTasksForm.controls['taskDetails'] as FormGroup).controls[
      this.getDriverPassengerFormKey(driverTaskName, detailsId)].get('isActive') as FormControl;
  }


  // getDriverSummaryDisplayName(driverSummary: DispatchDriverSummary): string{
  //   return `${driverSummary.driverFirstName?.charAt(0).toUpperCase()}. ${driverSummary.driverLastName}`
  // }

  setExpandedpanel(data: SearchSummary): void {
    this.expandedPanel = data.taskName;
    this.driverTasksForm.get('cordNotes').setValue(data.specialInstructions);
    this.driverTasksForm.get('driverNotes').setValue(data.driverNotes === 'null' ? '' : data.driverNotes);
  }
  
  private getErrMsg(): string {
    const formData = this.searchForm.value;
    switch (formData.searchCategory) {
      case 'Flight':
        return '4 numbers max for flight search.';
      case 'Name':
        return 'Invalid name search, letters only please.';
      case 'Destination':
        return 'Invalid gateway search, 3 letters only please.';
      case 'Tail':
        return 'Invalid tail search, letters and numbers only please. Must be between 3 and 10 characters.';
    }
    return null;
  }

  private subscribeSearchFormChange(): void {
    this.searchForm.controls['searchTerm'].valueChanges.subscribe((value: any) => {
      if (this.searchForm.valid && value) {
        this.validationErrMsg = null;
      } else {
        this.validationErrMsg = this.getErrMsg();
      }
    });
  }

  onSearchCategoryChange(value: string): void {
    this.validationErrMsg = null;
    switch (value) {
      case 'Flight':
        this.searchForm = new FormGroup({
          searchCategory: new FormControl('Flight', Validators.nullValidator),
          airlinePrefix: new FormControl('UPS', Validators.required),
          searchTerm: new FormControl('',
          Validators.compose([
            Validators.pattern("^[0-9]*$"),
            Validators.maxLength(4)
          ]))
        });
        break;
        case 'Name':
          this.searchForm = new FormGroup({
            searchCategory: new FormControl('Name', Validators.nullValidator),
            airlinePrefix: new FormControl({ value: '', disabled: true }, []),
            searchTerm: new FormControl('',
          Validators.compose([
            Validators.pattern("^[a-zA-Z]*$"),
            Validators.maxLength(10)
          ]))
        });
        break;
        case 'Destination':
          this.searchForm = new FormGroup({
            searchCategory: new FormControl('Destination', Validators.nullValidator),
            airlinePrefix: new FormControl({ value: '', disabled: true }, []),
            searchTerm: new FormControl('',
            Validators.compose([
              Validators.pattern("^[a-zA-Z]*$"),
              Validators.maxLength(3)
            ]))
          });
          break;
          case 'Tail':
            this.searchForm = new FormGroup({
              searchCategory: new FormControl('Tail', Validators.nullValidator),
              airlinePrefix: new FormControl({ value: '', disabled: true }, []),
              searchTerm: new FormControl('',
              Validators.compose([
                Validators.pattern("^[a-zA-Z0-9]*$"),
                Validators.minLength(3),
                Validators.maxLength(10)
              ]))
        });
        break;
    }
  }
  
  onSubmit(): void {
    this.taskGroupSummaryList = [];
    const formData = this.searchForm.value;
    if(!this.searchForm.valid || !formData.searchTerm){
      console.error('Invalid inputs', this.searchForm);
      this.validationErrMsg=this.getErrMsg();
      return;
    }
    this.loading = true;
    this.validationErrMsg = null;
    console.log('searchForm values :; ', formData)
    // alert('On submit')
    const params = {
      noCache: new Date().getTime(),
      searchTerm: formData.searchTerm,
      searchCategory: formData.searchCategory,
      airlinePrefix: formData.airlinePrefix
    };
    this.searchService.searchData(params).subscribe(res => {
      console.log('Search result:', res.body);
      this.taskGroupSummaryList = res.body as SearchSummary[];
      this.updateDriverTaskForm(this.taskGroupSummaryList);
      this.updateDriverTaskFormAddOns(this.taskGroupSummaryList);
      // this.flightCustoms = this.events.uiParameters.FLIGHT_CUSTOMS;
      if (!this.taskGroupSummaryList || !this.taskGroupSummaryList.length) {
        this.toastService.show(`NO RESULTS FOUND`, { classname: 'bg-success text-light', delay: 6000 });
      }
      this.loading = false;
    });
  }

  passengerOnChange(e: any, person: Passenger) {
    console.log(e);
    console.log(person.id);
    console.log(
      'DRIVER TASK FORM SHOULD BE UPDATED HERE WITH PICKUP STATUS FORM CONTROLS',
      this.driverTasksForm.get('passengers').value
    );
  }

  assignTask(taskName: string){
    const adId = this.profileService.profileObs$.getValue().adId;
    if (!this.eventsService.driverHasVan(adId)) {
      this.toastService.show('You do not have a van assigned. Please get a van before assign tasks.', { classname: 'bg-danger text-light', delay: 8000 });
      return;
    }
    const assignTaskPayload: AssignTask = {
      assignTo: this.profileService.profileObs$.getValue().adId,
      taskIds: [taskName],
      type: "assign"
    }
    this.unassignedTasksService.assignTask(assignTaskPayload).subscribe({
      next: (response: HttpResponse<any>) => {
        if (response.status === 204) {
          // this.eventsDeltaService.getDeltas()
          this.onSubmit();
          this.eventsDeltaService.getDeltas()
          this.toastService.show(`TASK ASSIGNED SUCCESSFULLY`, { classname: 'bg-success text-light', delay: 6000 });
        } else {
          this.logger.error(`${response.status} ${response.statusText}`);
          this.toastService.show(`ERROR ${response.status}: TASK WAS NOT ASSIGNED`, { classname: 'bg-danger text-light', delay: 6000 });
        }
      },
      error: () => { /* handled by UnassignedTasksService.handleError — toast already shown */ }
    });
  }

  checkCustoms(origin): boolean{
    return this.flightCustoms.includes(origin);
  }

  onPassngerCheckedIn($event: MatSlideToggleChange): void {
    this.cdr.detectChanges();
  }

  showPickUpButtons(status): boolean {
      if(status === 'Completed' || status === "Assigned") {
        return false;
      }
      return status !== 'Started';
  }

  public canPickupSelected(driverTask: SearchSummary): boolean {
    if(!driverTask.passengers || !driverTask.passengers.length){
      return false;
    }
    for (const p of driverTask.passengers) {
      const checked = this.getPassengerFormControl(driverTask.taskName, p.id, driverTask.status).value
      console.log(p.firstName,">>", checked);
      if (checked) {
        return true;
      }
    }
    return false;
  }

  pickup(driverTask: SearchSummary, allOrSelected: PickUpDropOffType.PICKUP_ALL | PickUpDropOffType.PICKUP_SELECTED): void {
    this.pickUpDropOffAll = allOrSelected === PickUpDropOffType.PICKUP_ALL ? true : false;
    this.currentTaskToProcess = driverTask.taskName;
    const form = this.driverTasksForm.value;
    // are there passengers that are not checked in?
    if (driverTask.passengers && driverTask.passengers.length
      && driverTask.passengers.some((e) => e.checkedIn === false)) {
      const dialogRef = this.dialog.open(ConfirmPassengerNotCheckedInDialogComponent, {
        width: '350px',
        data: { driverTask: driverTask },
        disableClose: true
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) { // user clicked continue in modal
          this.markTaskAsStarted(driverTask, form, allOrSelected);
        } else {
          this.currentTaskToProcess = '';
          this.pickUpDropOffAll = false;
          this.loading = false;
        }
      });
    } else {
      this.markTaskAsStarted(driverTask, form, allOrSelected);
    }
  }

  extractPassengersFromForm(form: any, allOrSelected: string): string[]{
    let totalPassengers = 0;
    const passengers = [];
    const formPassengers = form.passengers;
    for (const [key, value] of Object.entries(formPassengers)) {
      totalPassengers++;
      const isChecked = value;
      const passengerId = key.slice(key.indexOf(":") + 1);
      if(allOrSelected === PickUpDropOffType.PICKUP_SELECTED || allOrSelected === PickUpDropOffType.DROPOFF_SELECTED){
        if(isChecked){
          passengers.push(passengerId);
        }
      }else{
        passengers.push(passengerId);
      }
      this.logger.debug(`isChecked: ${isChecked}, passengerId: ${passengerId}`, 'ui');
    }
    if(allOrSelected === PickUpDropOffType.DROPOFF_SELECTED && passengers.length < totalPassengers){
      this.partialPassengerDropoff = true;
    }
    return passengers;
  }

  extractAddOnsFromForm(form: any, allOrSelected: string, driverTask: SearchSummary): AddOnDetail[]{
    let totalAddOns = 0;
    const addOns = [];
    Object.keys(this.taskDetails['controls']).forEach((key, i) => {
      const addOnTaskId = key.split(':')[0];
      console.log(key);
      console.log(addOnTaskId);
      if(addOnTaskId === driverTask.taskName){
        const addOnId = key.slice(key.indexOf(":") + 1);
        const isChecked = this.taskDetails['controls'][key]['controls'].isActive.value;
         const obj: AddOnDetail = {
          type: this.taskDetails['controls'][key]['controls'].type.value,
          id: addOnId,
          name: this.taskDetails['controls'][key]['controls'].value.value
        }
        if(allOrSelected === PickUpDropOffType.PICKUP_SELECTED || allOrSelected === PickUpDropOffType.DROPOFF_SELECTED){
          if(isChecked){
            addOns.push(obj);
          }
        }else{
          addOns.push(obj);
        }
      }
    });
    if(allOrSelected === PickUpDropOffType.DROPOFF_SELECTED && addOns.length < totalAddOns){
      this.partialAddOnDropoff = true;
    }
    return addOns;
  }

  markTaskAsStarted(driverTask: SearchSummary, form: any, allOrSelected:  PickUpDropOffType.PICKUP_ALL | PickUpDropOffType.PICKUP_SELECTED) {
    const payload: PickUpTask = {
      taskIds: [driverTask.taskName],
      assignTo: this.profileService.profileObs$.getValue().adId,
      passengers: this.extractPassengersFromForm(form, allOrSelected),
      type: TaskActionType.PICKUP,
      driverNotes: form.driverNotes,
      addOnDetails: this.extractAddOnsFromForm(form, allOrSelected, driverTask)
    }
    console.log(payload);
    this.myTasksService.markTaskAsStarted(payload)
    .pipe(tap(() => this.eventsDeltaService.getDeltas()))
    .subscribe((resp:HttpResponse<any>) => {
      this.logger.debug(`PUT markTaskAsStarted response:`, resp, 'http');
       if(resp.status === 200){
        console.log(resp.body);
        driverTask.status = 'Started';
        driverTask.assignTo = this.profileService.profileObs$.getValue().adId;
        this.toastService.show(`Success: Task Updated`, { classname: 'bg-success text-light', delay: 4000 });
      }else{
        this.toastService.show(`Error: ${resp.status} occurred, task was not updated!`, { classname: 'bg-danger text-light', delay: 4000 });
      }
    });
  }

  dropoff(driverTask: SearchSummary, allOrSelected: PickUpDropOffType.DROPOFF_ALL | PickUpDropOffType.DROPOFF_SELECTED) {
    this.pickUpDropOffAll = allOrSelected === PickUpDropOffType.DROPOFF_ALL ? true : false;
    this.partialPassengerDropoff = false;
    this.partialAddOnDropoff = false;
    this.currentTaskToProcess = driverTask.taskName;
    console.log(driverTask);
    const form = this.driverTasksForm.value;
    const payload: DropOffTask = {
      taskIds: [driverTask.taskName],
      assignTo: this.profileService.profileObs$.getValue().adId,
      passengers: this.extractPassengersFromForm(form, allOrSelected),
      type: TaskActionType.DROPOFF,
      driverNotes: form.driverNotes,
      addOnDetails: this.extractAddOnsFromForm(form, allOrSelected, driverTask)
    }
    if(this.partialPassengerDropoff){
      this.toastService.show(`Error: A note is required when not dropping off all passengers.`, { classname: 'bg-danger text-light', delay: 4000 });
      this.currentTaskToProcess = '';
    }else if(this.partialAddOnDropoff){
      this.toastService.show(`Error: A note is required when not dropping off all add-ons.`, { classname: 'bg-danger text-light', delay: 4000 });
      this.currentTaskToProcess = '';
    }else{
      this.myTasksService.dropoffPassengers(payload)
      .pipe(tap(() => this.eventsDeltaService.getDeltas()))
      .subscribe((resp:HttpResponse<any>) => {
        this.logger.debug(`PUT dropoffPassengers response:`, resp, 'http');
        if(resp.status === 204){
          this.toastService.show(`Success: Task Updated`, { classname: 'bg-success text-light', delay: 4000 });
        }else{
          this.toastService.show(`Error: ${resp.status} occurred, task was not updated!`, { classname: 'bg-danger text-light', delay: 4000 });
        }
      });
    }
  }

  getDriverName(driverId: string): string {
    try {
      const driver: DispatchDriverSummary = this.driverSummaryList.find((driver: DispatchDriverSummary) => driver.driverId === driverId);
      if (driver) {
        return `${driver.driverFirstName} ${driver.driverLastName}`;
      }
    } catch (error) {
    }
    return '--';
  }
}
